import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Trash2, Settings2, ShieldCheck, Sparkles, Receipt, Cpu, TrendingUp, FileSignature, PenTool, ClipboardList, X, Check, Archive, ArchiveRestore } from "lucide-react";

export default function Team() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [accountManagers, setAccountManagers] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [permTarget, setPermTarget] = useState(null);
  const [includeArchived, setIncludeArchived] = useState(false);

  async function load() {
    const [u, am] = await Promise.all([
      api.get("/users", { params: includeArchived ? { include_archived: true } : {} }),
      api.get("/account-managers"),
    ]);
    setItems(u.data);
    setAccountManagers(am.data);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [includeArchived]);

  async function del(u) {
    const label = `PERMANENTLY delete user "${u.name}"? This cannot be undone.\n\nType DELETE to confirm.`;
    const ans = window.prompt(label);
    if (ans !== "DELETE") return;
    try { await api.delete(`/users/${u.id}`); load(); } catch (e) { alert(formatErr(e.response?.data?.detail)); }
  }
  async function archive(u) {
    if (!window.confirm(`Archive user "${u.name}"? They will no longer be able to log in.`)) return;
    try { await api.patch(`/users/${u.id}/archive`); load(); } catch (e) { alert(formatErr(e.response?.data?.detail)); }
  }
  async function unarchive(u) {
    try { await api.patch(`/users/${u.id}/unarchive`); load(); } catch (e) { alert(formatErr(e.response?.data?.detail)); }
  }
  async function changeRole(id, role) {
    try { await api.patch(`/users/${id}/role`, { role }); load(); }
    catch (e) { alert(formatErr(e.response?.data?.detail)); }
  }
  async function togglePerm(u, field, current) {
    try {
      await api.patch(`/users/${u.id}/permissions`, { [field]: !current });
      load();
    } catch (e) { alert(formatErr(e.response?.data?.detail)); }
  }

  return (
    <div className="px-5 py-4 max-w-7xl" data-testid="team-page">
      <div className="flex items-center gap-3 mb-3">
        <h1 className="text-lg font-serif-strong leading-none">User management</h1>
        <span className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-[var(--ink-2)]"><ShieldCheck size={11} strokeWidth={1.5} /> {items.length} users</span>
        <div className="flex-1" />
        {isAdmin && (
          <>
            <label className="flex items-center gap-1.5 text-[11px] text-[var(--ink-2)] cursor-pointer" data-testid="filter-users-archived">
              <input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} className="accent-[var(--navy)]" />
              Show archived
            </label>
            <button data-testid="new-user-btn" onClick={() => setOpenNew(true)} className="btn-primary flex items-center gap-1.5 text-xs !py-1.5 !px-2.5"><Plus size={12} strokeWidth={1.5} /> Add user</button>
          </>
        )}
      </div>

      <div className="card-flat overflow-x-auto">
        <table className="tbl tbl-slim">
          <thead>
            <tr>
              <th>User</th>
              <th className="w-28">Role</th>
              <th>Client visibility</th>
              {MODULES.map((m) => <th key={m.field} className="w-14 text-center" title={m.title}>{m.label}</th>)}
              <th className="w-20">Created</th>
              <th className="w-24" />
            </tr>
          </thead>
          <tbody>
            {items.map(u => (
              <UserRow key={u.id} u={u} me={user} isAdmin={isAdmin} onRole={changeRole} onToggle={togglePerm}
                onPerm={() => setPermTarget(u)} onArchive={() => archive(u)} onUnarchive={() => unarchive(u)} onDelete={() => del(u)} />
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-[var(--ink-2)] mt-2">Click a module chip to switch access on or off. Admins always have access to every module.</div>

      {openNew && <NewUserModal onClose={(s) => { setOpenNew(false); if (s) load(); }} />}
      {permTarget && (
        <PermissionsModal
          target={permTarget}
          accountManagers={accountManagers}
          onClose={(saved) => { setPermTarget(null); if (saved) load(); }}
        />
      )}
    </div>
  );
}

// field: user flag; defaultOn: AI is on unless explicitly disabled; adminAlways: admins bypass the flag.
const MODULES = [
  { field: "ai_enabled", label: "AI", title: "AI features", icon: Sparkles, defaultOn: true, adminAlways: false, tid: "ai" },
  { field: "finance_enabled", label: "Fin", title: "Finance & Billing", icon: Receipt, tid: "finance", adminAlways: true },
  { field: "frtx_enabled", label: "FRTX", title: "FRTX bookkeeping", icon: Cpu, tid: "frtx", adminAlways: true },
  { field: "sales_enabled", label: "Sales", title: "Sales CRM", icon: TrendingUp, tid: "sales", adminAlways: true },
  { field: "onboarding_enabled", label: "Onb", title: "Client Onboarding", icon: FileSignature, tid: "onboarding", adminAlways: true },
  { field: "fbsign_enabled", label: "Sign", title: "FBSign e-signature", icon: PenTool, tid: "fbsign", adminAlways: true },
  { field: "buildlog_enabled", label: "Log", title: "Build Log", icon: ClipboardList, tid: "buildlog", adminAlways: true },
];

function UserRow({ u, me, isAdmin, onRole, onToggle, onPerm, onArchive, onUnarchive, onDelete }) {
  const allowed = u.allowed_account_managers || [];
  const seesAll = u.role !== "member" || allowed.length === 0;
  const self = u.id === me?.id;
  return (
    <tr data-testid={`user-row-${u.id}`} className={u.archived ? "opacity-60" : ""}>
      <td>
        <div className="flex items-center gap-1.5">
          <span className="font-medium">{u.name}</span>
          {u.provider === "entra" && <Tag cls="border-[var(--navy)] text-[var(--navy)] bg-[var(--navy-soft)]">SSO</Tag>}
          {u.archived && <Tag cls="border-[var(--warn)] text-[var(--warn)] bg-[var(--gold-soft)]">Archived</Tag>}
          {self && <span className="text-[9px] text-[var(--ink-2)]">(you)</span>}
        </div>
        <div className="text-[10px] text-[var(--ink-2)]">{u.email}</div>
      </td>
      <td>
        {isAdmin && !self ? (
          <select data-testid={`role-select-${u.id}`} value={u.role} onChange={(e) => onRole(u.id, e.target.value)} className="input-slim !py-0.5 !pl-1.5 !pr-6 text-[11px] !w-[100px]">
            <option value="member">Member</option>
            <option value="manager">Manager</option>
            <option value="admin">Admin</option>
          </select>
        ) : (
          <span className="capitalize text-[var(--ink-2)]">{u.role}</span>
        )}
      </td>
      <td>
        {seesAll ? <span className="text-[var(--ink-2)]">All clients</span> : (
          <div className="flex flex-wrap gap-0.5 max-w-[220px]" data-testid={`user-am-${u.id}`}>
            {allowed.map((a) => <Tag key={a} cls="border-[var(--gold)] text-[var(--warn)] bg-[var(--gold-soft)]">{a}</Tag>)}
          </div>
        )}
      </td>
      {MODULES.map((m) => <td key={m.field} className="text-center"><ModuleChip m={m} u={u} canEdit={isAdmin && !(m.field === "ai_enabled" && self)} onToggle={onToggle} /></td>)}
      <td className="font-mono-data text-[10px] text-[var(--ink-2)] whitespace-nowrap">{(u.created_at || "").slice(0, 10)}</td>
      <td className="text-right whitespace-nowrap">
        {isAdmin && u.role === "member" && !u.archived && (
          <IconBtn tid={`perm-btn-${u.id}`} onClick={onPerm} title="Configure client visibility"><Settings2 size={12} strokeWidth={1.5} /></IconBtn>
        )}
        {isAdmin && !self && !u.archived && <IconBtn tid={`archive-user-${u.id}`} onClick={onArchive} title="Archive user"><Archive size={12} strokeWidth={1.5} /></IconBtn>}
        {isAdmin && !self && u.archived && <IconBtn tid={`unarchive-user-${u.id}`} onClick={onUnarchive} title="Unarchive user"><ArchiveRestore size={12} strokeWidth={1.5} /></IconBtn>}
        {isAdmin && !self && u.archived && <IconBtn tid={`del-user-${u.id}`} onClick={onDelete} title="Permanently delete (type DELETE)" danger><Trash2 size={12} strokeWidth={1.5} /></IconBtn>}
      </td>
    </tr>
  );
}

function ModuleChip({ m, u, canEdit, onToggle }) {
  const Icon = m.icon;
  const viaAdmin = m.adminAlways && u.role === "admin";
  const on = viaAdmin || (m.defaultOn ? u[m.field] !== false : u[m.field] === true);
  const cls = on ? "border-[var(--ok)] text-[var(--ok)] bg-[var(--ok-bg)]" : "border-[var(--line)] text-[var(--ink-2)] bg-white";
  const base = `inline-flex items-center justify-center w-7 h-5 rounded-sm border transition-colors ${cls}`;
  if (viaAdmin) return <span className={`${base} opacity-70`} title={`Admins always have ${m.title}`}><Icon size={10} strokeWidth={1.75} /></span>;
  if (!canEdit) return <span className={base} title={`${m.title}: ${on ? "on" : "off"}`}><Icon size={10} strokeWidth={1.75} /></span>;
  return (
    <button data-testid={`${m.tid}-toggle-${u.id}`} onClick={() => onToggle(u, m.field, on)} className={`${base} hover:border-[var(--navy)]`}
      title={`${m.title} ${on ? "enabled — click to disable" : "disabled — click to enable"}`} aria-pressed={on}>
      <Icon size={10} strokeWidth={1.75} />
    </button>
  );
}

function Tag({ cls, children }) {
  return <span className={`inline-block text-[8.5px] uppercase tracking-wider px-1 py-px rounded-sm border ${cls}`}>{children}</span>;
}

function IconBtn({ tid, onClick, title, danger, children }) {
  return (
    <button data-testid={tid} onClick={onClick} title={title}
      className={`p-1 rounded-sm transition-colors ${danger ? "text-[var(--err)] hover:bg-[var(--err-bg)]" : "text-[var(--ink-2)] hover:text-[var(--navy)] hover:bg-[var(--bg-2)]"}`}>
      {children}
    </button>
  );
}

function PermissionsModal({ target, accountManagers, onClose }) {
  const [allowed, setAllowed] = useState(new Set(target.allowed_account_managers || []));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  function toggle(am) {
    const next = new Set(allowed);
    next.has(am) ? next.delete(am) : next.add(am);
    setAllowed(next);
  }

  async function save() {
    setSaving(true); setErr("");
    try {
      await api.patch(`/users/${target.id}/permissions`, { allowed_account_managers: Array.from(allowed) });
      onClose(true);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail));
    } finally { setSaving(false); }
  }

  return (
    <>
      <div className="backdrop" onClick={() => onClose(false)} />
      <div className="drawer" data-testid="perm-modal">
        <div className="p-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="overline mb-1">Client visibility</div>
              <h2 className="font-display text-3xl">Permissions.</h2>
              <div className="text-sm text-[var(--ink-2)] mt-2"><span className="font-medium">{target.name}</span> will only see clients whose Account Manager is in the selected list. Leave empty to grant access to all clients.</div>
            </div>
            <button onClick={() => onClose(false)} className="text-[var(--ink-2)] hover:text-[var(--ink)]"><X size={16} strokeWidth={1.5} /></button>
          </div>

          <div className="mb-4">
            <label className="label-overline">Allowed Account Managers</label>
            {accountManagers.length === 0 ? (
              <div className="text-sm text-[var(--ink-2)]">No Account Managers found yet. Add some to clients first.</div>
            ) : (
              <div className="flex flex-wrap gap-2 mt-2" data-testid="perm-am-grid">
                {accountManagers.map((am) => {
                  const on = allowed.has(am);
                  return (
                    <button
                      key={am}
                      data-testid={`perm-am-${am}`}
                      onClick={() => toggle(am)}
                      className={`status-pill flex items-center gap-1.5 ${on ? "border-[var(--gold)] text-[var(--warn)] bg-[var(--gold-soft)]" : "border-[var(--line)] text-[var(--ink-2)]"}`}
                    >
                      {on && <Check size={11} strokeWidth={1.5} />}
                      {am}
                    </button>
                  );
                })}
              </div>
            )}
            <div className="text-[11px] text-[var(--ink-2)] mt-3">
              {allowed.size === 0 ? "Empty → full access to all clients" : `${allowed.size} of ${accountManagers.length} selected`}
            </div>
          </div>

          {err && <div className="text-sm text-[var(--err)] mb-3">{err}</div>}

          <div className="flex gap-2 mt-6">
            <button data-testid="perm-save" disabled={saving} onClick={save} className="btn-primary">{saving ? "Saving…" : "Save permissions"}</button>
            <button onClick={() => onClose(false)} className="btn-ghost">Cancel</button>
          </div>
        </div>
      </div>
    </>
  );
}

function NewUserModal({ onClose }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "member" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  async function save(e) {
    e.preventDefault();
    setSaving(true); setErr("");
    try { await api.post("/auth/register", form); onClose(true); }
    catch (e) { setErr(formatErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  }

  return (
    <>
      <div className="backdrop" onClick={() => onClose(false)} />
      <div className="drawer p-8" data-testid="new-user-modal">
        <form onSubmit={save}>
          <div className="overline mb-2">Invite</div>
          <h2 className="font-display text-3xl mb-8">New user.</h2>
          {err && <div className="text-sm text-[var(--err)] mb-3">{err}</div>}
          <F label="Name"><input data-testid="nu-name" required className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></F>
          <F label="Email"><input data-testid="nu-email" type="email" required className="input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></F>
          <F label="Password"><input data-testid="nu-password" type="password" required className="input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></F>
          <F label="Role"><select data-testid="nu-role" className="input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>{["member", "manager", "admin"].map(r => <option key={r}>{r}</option>)}</select></F>
          <div className="flex gap-2"><button data-testid="nu-save" disabled={saving} className="btn-primary">{saving ? "Saving…" : "Create"}</button><button type="button" onClick={() => onClose(false)} className="btn-ghost">Cancel</button></div>
        </form>
      </div>
    </>
  );
}
function F({ label, children }) { return (<div className="mb-3"><label className="label-overline">{label}</label>{children}</div>); }
